/**
 * RealtimeBanner — pulsende notification der vises øverst i pakkeshop-portalen
 * når en pakke ankommer eller en retur opstår, drevet af Server-Sent Events.
 *
 * Auto-skjules efter 12s, kan lukkes manuelt, og spiller en kort beep.
 */
import React from "react";
import { Bell, X } from "lucide-react";

const AUTO_HIDE_MS = 12000;

export default function RealtimeBanner({ event, onDismiss, onJumpToTab }) {
  // event = { event, title, body, tab, booking_number } or null
  React.useEffect(() => {
    if (!event) return undefined;
    const t = setTimeout(() => onDismiss && onDismiss(), AUTO_HIDE_MS);
    // Try a short beep (best-effort — browsers gate AudioContext until user gesture)
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
        o.start();
        o.stop(ctx.currentTime + 0.5);
        setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 700);
      }
    } catch { /* ignore audio failures */ }
    return () => clearTimeout(t);
  }, [event, onDismiss]);

  if (!event) return null;
  const isReturn = event.event === "return_added";
  const accent = isReturn ? "#F59E0B" : "#DC2626";
  return (
    <div
      data-testid="shop-realtime-banner"
      role="status"
      aria-live="polite"
      className="fixed top-4 right-4 z-[60] max-w-sm w-[calc(100vw-2rem)] sm:w-96 rounded-xl overflow-hidden border-2 border-white"
      style={{
        background: "#0A1128",
        color: "#fff",
        boxShadow: `8px 8px 0 ${accent}E6`,
        animation: "shop-banner-in 320ms cubic-bezier(.18,.89,.32,1.28) both",
      }}
    >
      <style>{`
        @keyframes shop-banner-in {
          from { transform: translateY(-12px) scale(.96); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes shop-bell-pulse {
          0%, 100% { transform: rotate(0deg) scale(1); }
          15% { transform: rotate(-12deg) scale(1.05); }
          30% { transform: rotate(10deg) scale(1.05); }
          45% { transform: rotate(-6deg) scale(1.02); }
          60% { transform: rotate(4deg) scale(1); }
        }
      `}</style>
      <div className="flex items-stretch">
        <div
          className="flex items-center justify-center w-14 shrink-0"
          style={{ background: accent }}
        >
          <Bell
            className="w-7 h-7"
            style={{ animation: "shop-bell-pulse 1.4s ease-in-out infinite" }}
            aria-hidden="true"
          />
        </div>
        <div className="flex-1 p-3 pr-2 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">
            {isReturn ? "Ny retur" : "Pakke ankommet"} · Live
          </div>
          <div className="font-display font-black text-lg leading-tight mt-0.5 truncate">
            {event.title || (isReturn ? "Ny retur" : "Ny pakke")}
          </div>
          <div className="text-sm opacity-90 mt-0.5 line-clamp-2">
            {event.body || ""}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => onJumpToTab && onJumpToTab(event.tab || "parcels")}
              data-testid="shop-realtime-jump"
              className="text-xs font-bold px-3 py-1.5 rounded-md"
              style={{ background: "#fff", color: "#0A1128" }}
            >
              Se nu
            </button>
            <button
              type="button"
              onClick={onDismiss}
              data-testid="shop-realtime-dismiss"
              className="text-xs font-medium px-3 py-1.5 rounded-md border border-white/30 hover:bg-white/10"
            >
              Skjul
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-1 right-1 p-1 text-white/60 hover:text-white"
          aria-label="Luk"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
